import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '@nf/ui';
import { Icon, ArrowRightIcon } from '@/components/icons/svg-icons';
import { BasicModalHeader, ScrollableModal } from '@/components/widgets/scrollable-modal';
import { scoreChallengeModalService } from '@/services/cwc-jackpot/cwc-jackpot-modal.service';
import { CdnImg } from '@/features/cdn-img';
import clsx from 'clsx';
import styles from './score-challenge-rules-popup.module.scss';
import { useCwcJackpotRulesPopupViewedStatus } from '@/hooks/query/cwc-jackpot/use-cwc-jackpot-rules-popup-viewed-status';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote } from '@nf/utils-common/constants';
import { usePathname } from 'next/navigation';
import { AutoPopupKey, useRegisterAutoPopup } from '@nf/auto-popup-handler';

type TabType = 'howtoplay' | 'moredetails';
type FeedbackType = 'like' | 'dislike' | null;

export const ScoreChallengeRulesPopup = () => {
	const t = useTranslations('General');
	const [activeTab, setActiveTab] = useState<TabType>('howtoplay');
	const [feedbackStatus, setFeedbackStatus] = useState<FeedbackType>(null);
	const [wantsToShow, setWantsToShow] = useState(false);
	const canShow = useRegisterAutoPopup({
		key: AutoPopupKey.ScoreChallengeRules,
		isEnabled: wantsToShow,
	});
	const { data: viewedStatus, isLoading, setViewed } = useCwcJackpotRulesPopupViewedStatus();
	const pathname = usePathname();

	useEffect(() => {
		const subscription = scoreChallengeModalService.scoreChallengeRulesModal$.subscribe((isShow) => {
			setWantsToShow(isShow);
		});
		return () => subscription.unsubscribe();
	}, []);
	
	useEffect(() => {
		if (pathname.includes('score-challenge') && !viewedStatus?.IsViewed && !isLoading) {
			setWantsToShow(true);
		} 
	}, [viewedStatus, isLoading, pathname]);

	const closeRulesPopup = () => {
		setWantsToShow(false);
		setFeedbackStatus(null);
		if (!isLoading && viewedStatus && !viewedStatus.IsViewed) {
			setViewed();
		}
	}

	const handleTabClick = (tab: TabType) => () => {
		setKafkaNormal({
			note: KafkaNote.CWCJackpot,
			type: 1,
			action: tab === 'moredetails' ? 15 : 16
		});
		setFeedbackStatus(null);
		setActiveTab(tab);
	}

	const handleFeedbackClick = (type: FeedbackType) => () => {
		setFeedbackStatus(type);
	}

	const handleClickPlayNow = async () => {
		setKafkaNormal({
			note: KafkaNote.CWCJackpot,
			type: 1,
			action: 17
		});
		closeRulesPopup();
	}

	const tabItems = [
		{
			label: t('lbl_CWCHowToPlay'),
			value: 'howtoplay'
		},
		{
			label: t('lbl_CWCMoreDetails'),
			value: 'moredetails'
		}
	]

	return (
		<ScrollableModal
			isOpen={canShow}
			closeModal={closeRulesPopup}
			className={styles.modalContainer}
			header={(
				<>
					<BasicModalHeader
						className={styles.header}
						title={t('lbl_CWCRR')}
						closeModal={closeRulesPopup}
					/>
					<div className={styles.tabContainer}>
						<div className={styles.tabWrapper}>
							{tabItems.map((item) => (
								<div
									className={styles.tabItem}
									data-selected={activeTab === item.value}
									onClick={handleTabClick(item.value as TabType)}
									key={item.value}
								>
									{item.label}
								</div>
							))}
						</div>
					</div>
				</>
			)}
			footer={(
				<div className={styles.footer}>
					<button
						className={styles.betButton}
						onClick={handleClickPlayNow}
					>
						<span>{t('lbl_CWCPlayNow')}</span>
						<Icon name="arrow-right" />
					</button>
				</div>
			)}
		>
			<section className={styles.rulesContent}>
				{activeTab === 'howtoplay' ? (
					<HowToPlayContent />
				) : (
					<MoreDetailsContent />
				)}
				<div className={styles.feedbackContainer}>
					<div className={styles.feedbackTitle}>{t('lbl_CWCRules07')}</div>
					<div className={styles.feedbackButtons}>
						<Button
							variant="icon"
							className={styles.feedbackButton}
							data-selected={feedbackStatus === 'like'}
							onClick={handleFeedbackClick('like')}
						>
							<Icon name="thumb-up" />
						</Button>
						<Button
							variant="icon"
							className={styles.feedbackButton}
							data-selected={feedbackStatus === 'dislike'}
							onClick={handleFeedbackClick('dislike')}
						>
							<Icon name="thumb-down" />
						</Button>
					</div>
				</div>
			</section>
		</ScrollableModal>
	)
};

const HowToPlayContent = () => {
	const t = useTranslations('General');
	return (
		<div className={styles.stepContainer}>
			<div className={styles.stepItem}>
				<div className={styles.stepTitle}>
					<span className={styles.stepNumber}>1</span>
					<span className={styles.stepText}>{t('lbl_CWCRules01')}</span>
				</div>
				<div className={styles.stepDescription}>
					{t('lbl_CWCRules02')}
				</div>
				<div className={styles.stepImage}>
					<CdnImg
						src="/images/promotion/score-challenge/rules-popup/howtoplay-step1.png"
						alt="howtoplay-step1"
						width={264}
						height={164}
						isGalaxyFolder={true}
					/>
					<div className={clsx(styles.predictBtn, styles.predictBtn1)}>
						{t('lbl_CWCBanner03')}
						<ArrowRightIcon />
					</div>
					<div className={clsx(styles.predictBtn, styles.predictBtn2)}>
						{t('lbl_CWCBanner03')}
						<ArrowRightIcon />
					</div>
					<div className={clsx(styles.predictBtn, styles.predictBtn3)}>
						{t('lbl_CWCBanner03')}
						<ArrowRightIcon />
					</div>
				</div>
			</div>

			<div className={styles.stepItem}>
				<div className={styles.stepTitle}>
					<span className={styles.stepNumber}>2</span>
					<span className={styles.stepText}>{t('lbl_CWCRules03')}</span>
				</div>
				<div className={styles.stepDescription}>
					{t('lbl_CWCRules04')}
				</div>
				<div className={styles.stepImage}>
					<CdnImg
						src="/images/promotion/score-challenge/rules-popup/howtoplay-step2.png"
						alt="howtoplay-step2"
						width={264}
						height={148}
						isGalaxyFolder={true}
					/>
					<div className={styles.betSlipText}>
						{t('Betsilp')}
					</div>
					<div className={styles.scoreText}>
						{t('lbl_180')} / {t('lbl_CrctScore')}
					</div>
					<div className={styles.currencyText}>
						<span className={styles.currencyText1}>MYR</span>
						<span className={styles.currencyText2}>15</span>
					</div>
					<div className={styles.betBtn}>
						{t('lbl_bet')}
					</div>
					<div className={styles.jackpotText}>
						<CdnImg
							src="/images/promotion/score-challenge/rules-popup/icon-jackpot.svg"
							alt="icon-jackpot"
							width={20}
							height={20}
							isGalaxyFolder={true}
						/>
						{t('lbl_CWCBetSlipNotify02')}
					</div>
				</div>
			</div>

			<div className={styles.stepItem}>
				<div className={styles.stepTitle}>
					<span className={styles.stepNumber}>3</span>
					<span className={styles.stepText}>{t('lbl_CWCRules05')}</span>
				</div>
				<div className={styles.stepDescription}>
					{t('lbl_CWCRules06')}
				</div>
				<div className={styles.stepImage}>
					<CdnImg
						src="/images/promotion/score-challenge/rules-popup/howtoplay-step3.png"
						alt="howtoplay-step3"
						width={264}
						height={215}
						isGalaxyFolder={true}
					/>
					<div className={styles.wonText}>
						{t('lbl_CWCPrizeStatus01')}
						<CdnImg
							src="/images/promotion/score-challenge/rules-popup/icon-celebration.svg"
							alt="icon-celebration"
							width={14}
							height={14}
							isGalaxyFolder={true}
						/>
					</div>
					<div className={styles.matchText}>
						{t('lbl_CWCPoolMatch')}
					</div>
					<div className={clsx(styles.eligibleText, styles.eligibleText1)}>
						{t('lbl_CWCEligible')}
					</div>
					<div className={clsx(styles.eligibleText, styles.eligibleText2)}>
						{t('lbl_CWCEligible')}
					</div>
					<div className={clsx(styles.eligibleText, styles.eligibleText3)}>
						{t('lbl_CWCEligible')}
					</div>
					<div className={clsx(styles.hitText, styles.hitText1)}>
						{t('lbl_CWCHit')}
					</div>
					<div className={clsx(styles.hitText, styles.hitText2)}>
						{t('lbl_CWCHit')}
					</div>
					<div className={clsx(styles.hitText, styles.hitText3)}>
						{t('lbl_CWCHit')}
					</div>
				</div>
			</div>
		</div>
	);
};

const MoreDetailsContent = () => {
	const t = useTranslations('General');

	return (
		<>
			<div className={styles.rulesTitle}>
				<span>{t('lbl_CWCRR01')}</span>
				<span>{t('lbl_CWCRR02')}</span>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_CWCRR03')}</div>
				<div className={styles.rulesDescription}>
					<span>{t('lbl_CWCRR04')}</span>
					<span>{t('lbl_CWCRR05')} {t('lbl_CWCRR06')}</span>
				</div>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_CWCRR07')}</div>
				<ul>
					<li>{t('lbl_CWCRR08')}</li>
					<li>{t('lbl_CWCRR09')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_CWCRR10')}</div>
				<ul>
					<li>{t('lbl_CWCRR11')}</li>
					<li>{t('lbl_CWCRR12')}</li>
					<li>{t('lbl_CWCRR13')}</li>
					<li>{t('lbl_CWCRR14')}</li>
					<li>{t('lbl_CWCRR15')}</li>
					<li>{t('lbl_CWCRR16')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_CWCRR17')}</div>
				<ul>
					<li>{t('lbl_CWCRR18')}</li>
					<li>{t('lbl_CWCRR19')}</li>
					<li>{t('lbl_CWCRR20')}</li>
					<li>{t('lbl_CWCRR21')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_CWCRR22')}</div>
				<ul>
					<li>{t('lbl_CWCRR23')}</li>
					<li>{t('lbl_CWCRR24')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_CWCRR25')}</div>
				<ul>
					<li>{t('lbl_CWCRR26')}</li>
					<li>{t('lbl_CWCRR27')}</li>
					<li>{t('lbl_CWCRR28')}</li>
					<li>{t('lbl_CWCRR29')}</li>
					<li>{t('lbl_CWCRR30')}</li>
					<li>{t('lbl_CWCRR31')}</li>
					<li>{t('lbl_CWCRR32')}</li>
					<li>{t('lbl_CWCRR33')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_CWCRR34')}</div>
				<ul>
					<li>{t('lbl_CWCRR35')}</li>
					<li>{t('lbl_CWCRR36')}</li>
					<li>{t('lbl_CWCRR37')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_CWCRR38')}</div>
				<ul>
					<li>{t('lbl_CWCRR39')}</li>
					<li>{t('lbl_CWCRR40')}</li>
					<li>{t('lbl_CWCRR41')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_CWCRR42')}</div>
				<ul>
					<li>{t('lbl_CWCRR43')}</li>
					<li>{t('lbl_CWCRR44')}</li>
					<li>{t('lbl_CWCRR45')}</li>
					<li>{t('lbl_CWCRR46')}</li>
				</ul>
			</div>

			<div className={styles.rulesSection}>
				<div className={styles.sectionTitle}>{t('lbl_CWCRR47')}</div>
				<ul>
					<li>{t('lbl_CWCRR48')}</li>
					<li>{t('lbl_CWCRR49')}</li>
					<li>{t('lbl_CWCRR50')}</li>
					<li>{t('lbl_CWCRR51')}</li>
				</ul>
			</div>

			<div className={styles.disclaimerText}>{t('lbl_CWCRR52')}</div>
		</>
	);
};